// Email Templates Tab — Tenant Admins customise system templates
import React, { useState, useEffect } from 'react';

const TEMPLATE_NAMES = {
  new_invoice: 'New Invoice',
  new_invoice_statement: 'New Invoice Statement',
  hip_numbers: 'Hip Numbers',
  ship_in_note: 'Ship-In Note',
  deliverables_nudge: 'Deliverables Nudge',
  new_report: 'New Report',
  new_report_dsu: 'New Report – Daily Sale Update',
  seller_thank_you: 'Seller Thank You',
  buyer_thank_you: 'Buyer Thank You',
  document_for_signature_horse: 'Document for Signature (Horse)',
  document_for_signature_sale: 'Document for Signature (Sale)',
  task_assignment: 'Task Assignment',
  task_reminder: 'Task Reminder',
  event_reminder: 'Event Reminder',
  payment_confirmation: 'Payment Confirmation',
};
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Loader2, Save, Mail, Edit, RotateCcw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../services/api';
import RichTextEditor from '../ui/RichTextEditor';
import TemplateVariablesCard from '../shared/TemplateVariablesCard';

const EmailTemplatesTab = ({ tenantId }) => {
  const [loading, setLoading] = useState(true);
  const [customizableTemplates, setCustomizableTemplates] = useState([]);
  const [tenantOverrides, setTenantOverrides] = useState({});
  const [customVariables, setCustomVariables] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [form, setForm] = useState({ subject: '', body: '' });

  useEffect(() => {
    fetchTemplates();
    api.get('/system-settings/template-variables').then(r => setCustomVariables(r.data || [])).catch(() => {});
  }, [tenantId]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get('/email-templates');
      const all = res.data || [];
      // System templates that tenants can customise
      const customizable = all.filter(t => !t.tenant_id && t.tenant_customizable);
      // This tenant's overrides: keyed by template_type
      const overrides = {};
      all.filter(t => t.tenant_id).forEach(t => { overrides[t.template_type] = t; });
      setCustomizableTemplates(customizable.sort((a, b) => (a.name || a.template_type).localeCompare(b.name || b.template_type)));
      setTenantOverrides(overrides);
    } catch { toast.error('Failed to load templates'); }
    setLoading(false);
  };

  const handleEdit = (systemTemplate) => {
    const override = tenantOverrides[systemTemplate.template_type];
    setSelectedType(systemTemplate.template_type);
    setForm({
      subject: override?.subject ?? systemTemplate.subject ?? '',
      body: override?.body ?? systemTemplate.body ?? '',
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.subject.trim() || !form.body.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/email-templates/my-override/${selectedType}`, {
        subject: form.subject,
        body: form.body,
      });
      toast.success('Template saved — your version will be used for all future emails');
      setEditDialogOpen(false);
      fetchTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save template');
    }
    setSaving(false);
  };

  const handleReset = async (templateType) => {
    setResetting(templateType);
    try {
      await api.delete(`/email-templates/my-override/${templateType}`);
      toast.success('Reset to system default');
      fetchTemplates();
    } catch { toast.error('Failed to reset'); }
    setResetting(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  if (!customizableTemplates.length) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Mail className="w-10 h-10 mx-auto mb-3 opacity-20" />
        <p className="text-sm">No customizable email templates available.</p>
        <p className="text-xs mt-1">Your System Owner can enable templates for tenant customisation.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Template Variables reference card — collapsible, default closed */}
      <TemplateVariablesCard tenantLabel="Tenant (You)" collapsible defaultOpen={false} customVariables={customVariables} />

      <p className="text-sm text-muted-foreground">
        Customise the email templates below. Your changes override the system defaults for all emails sent from your account.
      </p>
      <div className="grid gap-3">
        {customizableTemplates.map(sysTemplate => {
          const override = tenantOverrides[sysTemplate.template_type];
          const isCustomised = !!override;
          return (
            <Card key={sysTemplate.id} className={isCustomised ? 'border-primary/30' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <p className="font-medium text-sm">{sysTemplate.name || TEMPLATE_NAMES[sysTemplate.template_type] || sysTemplate.template_type}</p>
                      {isCustomised
                        ? <Badge className="bg-[rgba(30,81,40,0.1)] text-primary text-xs pointer-events-none">Customised</Badge>
                        : <Badge variant="outline" className="text-xs pointer-events-none">System Default</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground ml-6">
                      Subject: {(override?.subject || sysTemplate.subject || '—').slice(0, 80)}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {isCustomised && (
                      <Button
                        variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground"
                        onClick={() => handleReset(sysTemplate.template_type)}
                        disabled={resetting === sysTemplate.template_type}
                        title="Reset to system default"
                      >
                        {resetting === sysTemplate.template_type
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <><RotateCcw className="w-3.5 h-3.5 mr-1" />Reset</>}
                      </Button>
                    )}
                    <Button
                      size="sm" variant="outline" className="h-8 text-xs"
                      onClick={() => handleEdit(sysTemplate)}
                    >
                      <Edit className="w-3.5 h-3.5 mr-1" />
                      {isCustomised ? 'Edit' : 'Customise'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={v => { if (!v) setEditDialogOpen(false); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {customizableTemplates.find(t => t.template_type === selectedType)?.name || TEMPLATE_NAMES[selectedType] || selectedType}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Subject Line</Label>
              <Input
                value={form.subject}
                onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                placeholder="Email subject..."
              />
            </div>
            <div className="space-y-2">
              <Label>Body</Label>
              <RichTextEditor
                value={form.body}
                onChange={body => setForm(f => ({ ...f, body }))}
                minHeight="300px"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Use <code className="bg-muted px-1 rounded">{'{variable_name}'}</code> for dynamic content.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Template</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplatesTab;
